import React, { useState, useRef, forwardRef } from 'react';
import {
    PopoverBase,
    PopoverBaseProps,
    Drawer,
    useBreakpoint,
} from '@landr/maestro';
import { noop } from 'utils/noop';
import CollectionsListPopover, {
    CollectionsListPopoverProps,
} from 'components/CollectionsListPopover';
import { AnalyticsSource } from 'types';
import { Sample } from 'types/generated/graphql';

type AddToCollectionProps = {
    samples: Sample[];
    placeholder?: string;
};

type CollectionsListProps = CollectionsListPopoverProps & AddToCollectionProps;

export function useOpenAddToCollectionPrompt(): {
    openAddToCollection: (
        samples: Sample[],
        source: AnalyticsSource,
        placeholder?: string,
    ) => void;
    AddToCollection: React.FC<PopoverBaseProps & AddToCollectionProps>;
    anchorRef: React.MutableRefObject<null>;
    CollectionsList: React.ForwardRefExoticComponent<
        CollectionsListProps & React.RefAttributes<HTMLDivElement>
    >;
} {
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const openAddToCollection = (
        samples: Sample[],
        source: AnalyticsSource,
    ): void => {
        setIsOpen(true);
    };

    const handleOnClick = (e: React.MouseEvent): void => {
        e.stopPropagation();
    };
    const isMobile = useBreakpoint('md');

    const CollectionsList = forwardRef<HTMLDivElement, CollectionsListProps>(
        ({ samples, placeholder, ...props }, ref) => {
            const sampleIds: string[] = samples.map(
                (sample: Sample) => sample.guid,
            );

            return (
                <CollectionsListPopover
                    ref={ref}
                    sampleIds={sampleIds}
                    placeholder={placeholder}
                    onClick={handleOnClick}
                    {...props}
                />
            );
        },
    );

    const close = (): void => setIsOpen(false);

    const AddToCollectionDrawer: React.FC<AddToCollectionProps> = ({
        samples,
        placeholder,
    }) => (
        <Drawer isVisible={isOpen} onClose={close}>
            <Drawer.Content>
                <CollectionsList
                    samples={samples}
                    placeholder={placeholder}
                    hide={noop}
                />
            </Drawer.Content>
        </Drawer>
    );

    const AddToCollectionPopover: React.FC<
        PopoverBaseProps & AddToCollectionProps
    > = ({ samples, placeholder, ...popoverProps }) => (
        <PopoverBase
            isOpen={isOpen}
            onClickOutside={close}
            py="none"
            px="none"
            {...popoverProps}
        >
            <CollectionsList
                samples={samples}
                placeholder={placeholder}
                hide={close}
            />
        </PopoverBase>
    );

    const AddToCollection: React.FC<PopoverBaseProps & AddToCollectionProps> = (
        props,
    ) => {
        const { samples, placeholder, ...popoverProps } = props;

        return (
            <>
                {isMobile && (
                    <AddToCollectionDrawer
                        samples={samples}
                        placeholder={placeholder}
                    />
                )}
                {!isMobile && (
                    <AddToCollectionPopover
                        samples={samples}
                        placeholder={placeholder}
                        {...popoverProps}
                    />
                )}
            </>
        );
    };

    return { openAddToCollection, AddToCollection, anchorRef, CollectionsList };
}
